import { CLIENT_ENVS } from '@/helpers/constants';

/**
 * Feature flag to enable/disable email validation
 * Set NEXT_RUNTIME_PUBLIC_ABSTRACT_API_KEY to enable/disable validation
 */
export const IS_EMAIL_VALIDATION_ENABLED = !!CLIENT_ENVS.ABSTRACT_API_KEY;

/**
 * Whitelist of trusted email domains that don't require temporary email validation.
 * These domains are exempt from AbstractAPI validation to minimize costs.
 */
export const WHITELISTED_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'levelupcasino.com',
  'yahoo.com',
  'web.de',
  'gmx.de',
  'yahoo.com.au',
  'hotmail.com.au',
  'outlook.de',
  'outlook.com.au',
  'live.com.au',
  'googlemail.com',
  'bigpond.com',
  'mail.com',
  'firemail.de',
  'live.com',
  'proton.me',
  'gmx.net',
  'freenet.de',
  'gmx.at',
  'duck.com',
  'yahoo.de',
  'hotmail.de',
  'myyahoo.com',
  'yahoo.ca',
  'aol.com',
  'gmail.com.au',
  'outlook.dk',
  'y7mail.com',
  't-online.de',
  'msn.com',
  'live.ca',
  'me.com',
  'hotmail.ca',
  'outlook.co.nz',
  'protonmail.com',
  'gmx.com',
  'mail.de',
  'wp.pl',
  'live.de',
  'op.pl',
  'gmx.ch',
  'mail.ru',
  'abv.bg',
  'ymail.com',
  'optusnet.com.au',
  'bigpond.net.au',
  'inbox.lv',
  'libero.it',
  'skymail.de',
  'seznam.cz',
  'email.com',
  'hotmail.co.uk',
  'hotmail.co.nz',
  'o2.pl',
  'outlook.fr',
  'windowslive.com',
  'rocketmail.com',
  'eclipso.de',
  'yahoo.co.nz',
  'rogers.com',
  'hotmail.fr',
  'hotmail.it',
  'live.dk',
  'onet.pl',
  'yahoo.co.uk',
  'bigpond.com.au',
  'tutamail.com',
  'yandex.com',
  'interia.pl',
  'outlook.it',
  'online.de',
  'gmail.de',
  'westnet.com.au',
  'iinet.net.au',
  'softswiss.com',
  'qq.com',
  'live.no',
  'telus.net',
  'yahoo.com.ph',
  'aol.de',
  'live.co.uk',
  'hotmail.fi',
  'naver.com',
  'email.cz',
  'shaw.ca',
  'freemail.hu',
  'my.com',
  'outlook.at',
  'outlook.ie',
  'live.at',
  'hotmail.no',
  'yahoo.ie',
  'pm.me',
  'gmx.fr',
  'vodafonemail.de',
  'yahoo.it',
  'bluewin.ch',
  'live.ie',
  'mail.ee',
  'bk.ru',
  'myself.com',
  'post.com',
  'vp.pl',
  'yahoo.dk',
  'yahoo.fr',
  'yandex.ru',
  'hotmail.dk',
  '163.com',
  'a1.net',
  'adam.com.au',
  'aol.com.au',
  'aon.at',
  'arcor.de',
  'bell.net',
  'bitmens.com',
  'blu.it',
  'byom.de',
  'centrum.cz',
  'centrum.sk',
  'cogeco.ca',
  'dodo.com.au',
  'dr.com',
  'e-mail.de',
  'eastlink.ca',
  'eclipso.at',
  'eclipso.ch',
  'email.de',
  'eq.edu.au',
  'existiert.net',
  'firemail.eu',
  'fmail.com',
  'freenet.com',
  'gmail.ca',
  'gmail.co.nz',
  'gmail.co.za',
  'gmail.com.nz',
  'gmx.ne',
  'go2.pl',
  'google.com',
  'hiepth.com',
  'hotmail.ch',
  'hotmail.gr',
  'hotmil.com',
  'icloud.com.au',
  'icloud.de',
  'ik.me',
  'ikmail.com',
  'inbox.eu',
  'inbox.lt',
  'inbox.ru',
  'int.pl',
  'interia.com',
  'interia.eu',
  'internet.ru',
  'internode.on.net',
  'iprimus.com.au',
  'kabelmail.de',
  'live.fr',
  'live.nl',
  'liwest.at',
  'mac.com',
  'magenta.de',
  'mail.bg',
  'mail.ch',
  'mailfence.com',
  'mask.me',
  'mechanicspedia.com',
  'meiliboxi.fi',
  'mozmail.com',
  'mun.ca',
  'netspace.net.au',
  'onet.eu',
  'online.no',
  'optusnet.com',
  'outlook.ca',
  'outlook.co.th',
  'outlook.com.gr',
  'outlook.es',
  'outlook.pt',
  'poczta.onet.pl',
  'post.cz',
  'privaterelay.appleid.com',
  'rambler.ru',
  'sasktel.net',
  'schools.sa.edu.au',
  'skiff.com',
  'skymesh.com.au',
  'spoko.pl',
  'sympatico.ca',
  'telstra.com',
  'tpg.com.au',
  'tuta.io',
  'tutanota.com',
  'ukr.net',
  'up.me',
  'upstars.com',
  'usa.com',
  'videotron.ca',
  'virgilio.it',
  'vmail.com',
  'vodafone.de',
  'wealthcapital.com.au',
  'xtra.co.nz',
  'yahoo.at',
  'yahoo.co.in',
  'yahoo.com.br',
  'yahoo.in',
  'ziggo.nl',
  'zohomail.com.au',
  'zohomail.eu',
]);

/**
 * Extracts the domain from an email address
 * @param email - The email address
 * @returns The domain part of the email, or undefined if invalid
 */
export const extractEmailDomain = (
  email: string | undefined
): string | undefined => {
  if (!email) return;

  const trimmedEmail = email.trim().toLowerCase();
  const atIndex = trimmedEmail.lastIndexOf('@');

  if (atIndex === -1 || atIndex === trimmedEmail.length - 1) {
    return;
  }

  return trimmedEmail.slice(atIndex + 1);
};

/**
 * Checks if an email domain is in the whitelist
 * @param email - The email address to check
 * @returns True if the domain is whitelisted, false otherwise
 */
export const isEmailDomainWhitelisted = (
  email: string | undefined
): boolean => {
  const domain = extractEmailDomain(email);
  if (!domain) return false;

  return WHITELISTED_EMAIL_DOMAINS.has(domain);
};
